import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  ExternalLink,
  Save,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  createNewDietPlanVersion,
  deleteDietPlanDraft,
  generateDietPlan,
  getDietFeedback,
  getDietPlan,
  getDietPlanHistory,
  getDietPlanInitialData,
  getNutritionKnowledgeBase,
  rebalanceDietPlan,
  saveDietPlan,
  saveNutritionKnowledgeBase,
  submitAdminDietFeedback,
} from '../../../api/dietPlans.api';
import MealPlanEditor from './components/MealPlanEditor';

const defaultPresentation = {
  title: 'رژیم اختصاصی Start by Azi',
  durationDays: 30,
  coachName: '',
  flexibleItems: '',
  supplements: '',
  importantNotes: '',
};
const macroPercentages = (targets = {}) => {
  const calories = Number(targets.targetCalories || 0);
  const macros = targets.macros || {};
  if (!calories) return { protein: 25, carb: 50, fat: 25 };
  const protein = Math.round(
    ((Number(macros.proteinGrams || 0) * 4) / calories) * 100,
  );
  const fat = Math.round(((Number(macros.fatGrams || 0) * 9) / calories) * 100);
  return { protein, fat, carb: Math.max(0, 100 - protein - fat) };
};
const macroGrams = (calories, percentages) => ({
  proteinGrams: Math.round(
    (calories * Number(percentages.protein || 0)) / 100 / 4,
  ),
  carbGrams: Math.round((calories * Number(percentages.carb || 0)) / 100 / 4),
  fatGrams: Math.round((calories * Number(percentages.fat || 0)) / 100 / 9),
});

export default function DietPlanTab({ userId }) {
  const [content, setContent] = useState('');
  const [knowledge, setKnowledge] = useState('');
  const [branding, setBranding] = useState({
    pdfHeaderColor: '#667b71',
    pdfHeaderImageUrl: '',
  });
  const [mealPlan, setMealPlan] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [settings, setSettings] = useState({
    snackCount: 3,
    budget: { targetCalories: '' },
    macroPercentages: { protein: 25, carb: 50, fat: 25 },
    presentation: defaultPresentation,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [rebalancing, setRebalancing] = useState(false);
  const [message, setMessage] = useState('');
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [manualFeedback, setManualFeedback] = useState('');
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [dietFeedback, setDietFeedback] = useState(null);
  const [showDietFeedback, setShowDietFeedback] = useState(false);
  const [history, setHistory] = useState([]);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [deletingDraft, setDeletingDraft] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [
          planResponse,
          knowledgeResponse,
          briefResponse,
          feedbackResponse,
        ] = await Promise.all([
          getDietPlan(userId),
          getNutritionKnowledgeBase(),
          getDietPlanInitialData(userId),
          getDietFeedback(userId),
        ]);
        const savedMealPlan = planResponse.data?.plan?.mealPlan || null;
        setContent(planResponse.data?.plan?.content || '');
        setMealPlan(savedMealPlan);
        setPdfUrl(planResponse.data?.plan?.pdfUrl || null);
        setActivePlan(planResponse.data?.plan || null);
        setKnowledge(knowledgeResponse.data?.knowledgeBase?.content || '');
        setBranding({
          pdfHeaderColor:
            knowledgeResponse.data?.knowledgeBase?.pdfHeaderColor || '#667b71',
          pdfHeaderImageUrl:
            knowledgeResponse.data?.knowledgeBase?.pdfHeaderImageUrl || '',
        });
        setInitialData(briefResponse.data);
        setDietFeedback(feedbackResponse.data?.feedback || null);
        setShowDietFeedback(Boolean(feedbackResponse.data?.awaitingNewDiet));
        const targets =
          savedMealPlan?.targets || briefResponse.data?.calculatedTargets;
        if (targets)
          setSettings({
            snackCount: savedMealPlan?.snackCount || 3,
            budget: { targetCalories: targets.targetCalories || '' },
            macroPercentages:
              savedMealPlan?.targets?.macroPercentages ||
              macroPercentages(targets),
            presentation: {
              ...defaultPresentation,
              ...(savedMealPlan?.presentation || {}),
            },
          });
      } catch {
        setMessage('دریافت اطلاعات رژیم انجام نشد.');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);
  useEffect(() => {
    getDietPlanHistory(userId)
      .then((result) => setHistory(result.data?.plans || []))
      .catch(() => setHistory([]));
  }, [userId]);

  const printableMealPlan = () =>
    mealPlan
      ? {
          ...mealPlan,
          snackCount: settings.snackCount,
          targets: {
            ...(mealPlan.targets || {}),
            targetCalories: Number(
              settings.budget.targetCalories ||
                mealPlan.targets?.targetCalories ||
                0,
            ),
            macros: macroGrams(
              Number(
                settings.budget.targetCalories ||
                  mealPlan.targets?.targetCalories ||
                  0,
              ),
              settings.macroPercentages,
            ),
            macroPercentages: settings.macroPercentages,
          },
          presentation: {
            ...(mealPlan.presentation || {}),
            ...settings.presentation,
          },
        }
      : null;
  const save = async (status) => {
    if (!content.trim()) return setMessage('ابتدا متن رژیم را وارد کنید.');
    setSaving(true);
    setMessage('');
    try {
      const result = await saveDietPlan(userId, {
        content,
        status,
        mealPlan: printableMealPlan(),
      });
      setActivePlan(result.data.plan);
      setMealPlan(result.data.plan.mealPlan || printableMealPlan());
      setPdfUrl(result.data.plan.pdfUrl || null);
      if (status === 'published') setShowDietFeedback(false);
      setMessage(
        status === 'published'
          ? 'رژیم برای کاربر منتشر شد.'
          : 'پیش‌نویس ذخیره شد.',
      );
    } catch (error) {
      setMessage(error.response?.data?.message || 'ذخیره رژیم انجام نشد.');
    } finally {
      setSaving(false);
    }
  };
  const generate = async () => {
    setGenerating(true);
    setMessage('');
    try {
      const result = await generateDietPlan(userId, settings);
      setActivePlan(result.data.plan);
      setContent(result.data.plan.content);
      setMealPlan(result.data.plan.mealPlan || null);
      setMessage('پیش‌نویس هوش مصنوعی آمادهٔ بازبینی است.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'تولید پیش‌نویس انجام نشد.');
    } finally {
      setGenerating(false);
    }
  };
  const rebalance = async (nextSettings = settings) => {
    if (!mealPlan)
      return setMessage(
        'ابتدا پیش‌نویس را بسازید؛ سپس تعداد وعده‌ها یا بودجه را به‌روزرسانی کنید.',
      );
    const total =
      Number(nextSettings.macroPercentages.protein || 0) +
      Number(nextSettings.macroPercentages.carb || 0) +
      Number(nextSettings.macroPercentages.fat || 0);
    if (Math.abs(total - 100) > 0.01)
      return setMessage('مجموع درصد درشت‌مغذی‌ها باید ۱۰۰٪ باشد.');
    setRebalancing(true);
    setMessage('');
    try {
      const result = await rebalanceDietPlan(userId, {
        mealPlan,
        settings: nextSettings,
      });
      setMealPlan(result.data.mealPlan);
      setPdfUrl(null);
      setMessage('جدول برنامه با بودجه و تعداد وعدهٔ جدید تنظیم شد.');
    } catch (error) {
      setMessage(
        error.response?.data?.message || 'به‌روزرسانی جدول انجام نشد.',
      );
    } finally {
      setRebalancing(false);
    }
  };
  const saveKnowledge = async () => {
    try {
      await saveNutritionKnowledgeBase({ content: knowledge, ...branding });
      setMessage('پایگاه دانش و ظاهر PDF ذخیره شد.');
    } catch {
      setMessage('ذخیره پایگاه دانش انجام نشد.');
    }
  };
  const saveManualFeedback = async () => {
    if (!manualFeedback.trim()) return;
    setFeedbackSaving(true);
    try {
      const result = await submitAdminDietFeedback(userId, manualFeedback);
      setDietFeedback(result.data?.feedback || null);
      setShowDietFeedback(true);
      setManualFeedback('');
      setMessage('فیدبک دستی ثبت شد و کاربر در انتظار رژیم جدید علامت خورد.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'ثبت فیدبک انجام نشد.');
    } finally {
      setFeedbackSaving(false);
    }
  };
  const createNewVersion = async (sourcePlanId = null) => {
    setCreatingVersion(true);
    setMessage('');
    try {
      const result = await createNewDietPlanVersion(userId, sourcePlanId);
      const plan = result.data.plan;
      setActivePlan(plan);
      setContent(plan.content || '');
      setMealPlan(plan.mealPlan || null);
      setPdfUrl(null);
      setHistory((items) => [
        plan,
        ...items.filter((item) => item._id !== plan._id),
      ]);
      setMessage(
        result.data.reusedDraft
          ? 'پیش‌نویس جدیدِ قبلی باز شد.'
          : result.data.copiedFromPrevious
            ? 'یک کپی قابل ویرایش از رژیم قبلی ساخته شد؛ تا تأیید نهایی برای کاربر ارسال نمی‌شود.'
            : 'پیش‌نویس خالی جدید ایجاد شد. تا تأیید نهایی برای کاربر ارسال نمی‌شود.',
      );
    } catch (error) {
      setMessage(
        error.response?.data?.message || 'ایجاد نسخهٔ جدید انجام نشد.',
      );
    } finally {
      setCreatingVersion(false);
    }
  };
  const deleteDraft = async () => {
    if (
      !activePlan?._id ||
      activePlan.status !== 'draft' ||
      !window.confirm(
        'این پیش‌نویس حذف شود؟ رژیم‌های منتشرشده و سوابق حذف نمی‌شوند.',
      )
    )
      return;
    setDeletingDraft(true);
    try {
      await deleteDietPlanDraft(userId, activePlan._id);
      setActivePlan(null);
      setContent('');
      setMealPlan(null);
      setPdfUrl(null);
      setHistory((items) =>
        items.filter((item) => item._id !== activePlan._id),
      );
      setMessage('پیش‌نویس حذف شد.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'حذف پیش‌نویس انجام نشد.');
    } finally {
      setDeletingDraft(false);
    }
  };
  if (loading)
    return <div className="h-72 animate-pulse rounded-2xl bg-[#f3efe7]" />;

  const assessment = initialData?.assessment || {};
  const weight = initialData?.weight || {};
  const client = initialData?.client || {};
  const dietaryWarnings = [
    assessment.foodAllergies,
    assessment.dietaryRestrictions,
    assessment.medicalConditions,
    assessment.medications,
    assessment.healthNotes,
  ]
    .filter(Boolean)
    .join(' | ');
  const updatePresentation = (key, value) =>
    setSettings((current) => ({
      ...current,
      presentation: { ...current.presentation, [key]: value },
    }));
  const totalMacroPercent =
    Number(settings.macroPercentages.protein || 0) +
    Number(settings.macroPercentages.carb || 0) +
    Number(settings.macroPercentages.fat || 0);
  const calculatedMacroGrams = macroGrams(
    Number(settings.budget.targetCalories || 0),
    settings.macroPercentages,
  );
  const updateSnackCount = (count) => {
    const next = { ...settings, snackCount: count };
    setSettings(next);
    void rebalance(next);
  };
  const percentageField = (key, label, gramKey) => (
    <label
      key={key}
      className="rounded-xl border border-[#dfe6dc] bg-white p-3">
      <span className="block text-[10px] font-bold text-[#71807a]">
        {label}
      </span>
      <div className="mt-1 flex items-end justify-between gap-2">
        <input
          type="number"
          min="1"
          max="98"
          className="w-20 border-0 bg-transparent p-0 text-lg font-black outline-none"
          value={settings.macroPercentages[key]}
          onChange={(event) =>
            setSettings((current) => ({
              ...current,
              macroPercentages: {
                ...current.macroPercentages,
                [key]: event.target.value,
              },
            }))
          }
        />
        <span className="text-xs font-bold text-[#315a38]">٪</span>
        <span className="text-[10px] text-[#74817a]">
          ≈ {calculatedMacroGrams[gramKey] || 0} گرم
        </span>
      </div>
    </label>
  );
  const brief = [
    ['قد', assessment.height ? `${assessment.height} سانتی‌متر` : '—'],
    [
      'وزن فعلی',
      weight.startingWeight
        ? `${weight.startingWeight} کیلوگرم`
        : assessment.weight
          ? `${assessment.weight} کیلوگرم`
          : '—',
    ],
    ['وزن هدف', weight.goalWeight ? `${weight.goalWeight} کیلوگرم` : '—'],
    ['هدف', assessment.mainGoal || client.goal || '—'],
    ['فعالیت', assessment.activityLevel || '—'],
    [
      'بیماری، دارو یا حساسیت',
      dietaryWarnings ||
        client.userNutritionData?.allergies ||
        'موردی ثبت نشده',
    ],
    ['ایمیل', client.email || '—'],
    ['شماره تماس', client.phoneNumber || '—'],
  ];

  return (
    <div dir="rtl" className="space-y-5">
      {showDietFeedback && dietFeedback?.text && (
        <section className="rounded-2xl border border-[#df6b52]/30 bg-[#fff5f0] p-4">
          <p className="text-xs font-black text-[#a44d39]">
            فیدبک دریافت‌شده از کاربر
          </p>
          <p className="mt-1 text-[11px] text-[#7b6259]">
            این فیدبک تا زمان انتشار نسخهٔ جدید در اینجا باقی می‌ماند.
          </p>
          <p className="mt-3 whitespace-pre-wrap rounded-xl border border-[#df6b52]/15 bg-white/75 p-3 text-sm leading-7 text-[#4d443f]">
            {dietFeedback.text}
          </p>
        </section>
      )}
      <section className="rounded-2xl border border-[#1c2c29]/10 bg-[#1c2c29] p-4 text-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-black tracking-wide text-[#efaa93]">
              مدیریت نسخه‌ها
            </p>
            <h3 className="mt-1 text-sm font-black">رژیم جدید، سابقهٔ محفوظ</h3>
            <p className="mt-1 text-xs leading-6 text-white/65">
              هیچ پیش‌نویسی به‌طور خودکار ارسال نمی‌شود؛ فقط «تأیید و انتشار»
              مربی نسخه را برای کاربر قابل مشاهده می‌کند.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => createNewVersion()}
              disabled={creatingVersion}
              className="rounded-xl border border-white/20 px-4 py-2.5 text-xs font-black text-white transition hover:bg-white/10 disabled:opacity-60">
              ایجاد رژیم جدید{' '}
            </button>
            <button
              type="button"
              onClick={() =>
                createNewVersion(
                  history.find((plan) => plan.status === 'published')?._id,
                )
              }
              disabled={
                creatingVersion ||
                !history.some((plan) => plan.status === 'published')
              }
              className="rounded-xl bg-[#df6b52] px-4 py-2.5 text-xs font-black text-white transition hover:bg-[#e67a62] disabled:opacity-60">
              {creatingVersion ? 'در حال ایجاد…' : 'کپی رژیم قبلی برای ویرایش'}
            </button>
            {activePlan?.status === 'draft' && (
              <button
                type="button"
                onClick={deleteDraft}
                disabled={deletingDraft}
                className="rounded-xl border border-red-300/70 px-4 py-2.5 text-xs font-black text-red-100 transition hover:bg-red-500/20 disabled:opacity-60">
                {deletingDraft ? 'در حال حذف…' : 'حذف پیش‌نویس'}
              </button>
            )}
          </div>
        </div>
      </section>
      <section className="rounded-2xl border border-[#d9e4d7] bg-[#f5f8f3] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black text-[#315a38]">
              بریف مربی پیش از ساخت رژیم
            </p>
            <p className="mt-1 text-xs text-[#68716d]">
              کالری و درصد درشت‌مغذی‌ها، بودجهٔ نهایی برنامه را می‌سازند.
            </p>
          </div>
          <label className="rounded-xl bg-white px-3 py-2 text-xs font-bold">
            تعداد میان‌وعده‌ها{' '}
            <select
              className="mr-2 bg-transparent outline-none"
              value={settings.snackCount}
              onChange={(event) =>
                updateSnackCount(Number(event.target.value))
              }>
              <option value={1}>۱</option>
              <option value={2}>۲</option>
              <option value={3}>۳</option>
            </select>
          </label>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {brief.map(([label, value]) => (
            <div
              key={label}
              className="rounded-xl border border-[#e1e7de] bg-white p-3">
              <p className="text-[10px] text-[#78827d]">{label}</p>
              <p className="mt-1 text-xs font-bold leading-5">{value}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-2xl border border-[#dfe6dc] bg-[#edf3eb] p-3">
          <div className="grid gap-2 sm:grid-cols-4">
            <label className="rounded-xl border border-[#315a38]/20 bg-[#19332d] p-3 text-white">
              <span className="block text-[10px] font-bold text-white/60">
                بودجهٔ روزانه (kcal)
              </span>
              <input
                type="number"
                min="1"
                className="mt-1 w-full border-0 bg-transparent p-0 text-lg font-black outline-none"
                value={settings.budget.targetCalories}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    budget: { targetCalories: event.target.value },
                  }))
                }
              />
            </label>
            {percentageField('protein', 'پروتئین', 'proteinGrams')}
            {percentageField('carb', 'کربوهیدرات', 'carbGrams')}
            {percentageField('fat', 'چربی', 'fatGrams')}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p
              className={`text-xs font-black ${Math.abs(totalMacroPercent - 100) < 0.01 ? 'text-[#315a38]' : 'text-[#b04c36]'}`}>
              جمع درصدها: {totalMacroPercent}٪{' '}
              {Math.abs(totalMacroPercent - 100) < 0.01
                ? '✓'
                : '— باید دقیقاً ۱۰۰٪ باشد'}
            </p>
            <button
              type="button"
              onClick={() => rebalance()}
              disabled={rebalancing || Math.abs(totalMacroPercent - 100) > 0.01}
              className="admin-secondary disabled:opacity-50">
              {rebalancing
                ? 'در حال تنظیم جدول…'
                : 'به‌روزرسانی جدول با این بودجه'}
            </button>
          </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <input
            className="admin-field !min-h-10"
            value={settings.presentation.title}
            onChange={(event) =>
              updatePresentation('title', event.target.value)
            }
            placeholder="عنوان رژیم"
          />
          <input
            className="admin-field !min-h-10"
            value={settings.presentation.coachName}
            onChange={(event) =>
              updatePresentation('coachName', event.target.value)
            }
            placeholder="نام مربی"
          />
          <label>
            <span className="mb-1 block text-[10px] font-bold text-[#71807a]">
              مدت رژیم (روز)
            </span>
            <input
              type="number"
              min="1"
              className="admin-field !min-h-10"
              value={settings.presentation.durationDays}
              onChange={(event) =>
                updatePresentation(
                  'durationDays',
                  Number(event.target.value) || 30,
                )
              }
            />
          </label>
          <textarea
            className="admin-field min-h-20"
            value={settings.presentation.flexibleItems}
            onChange={(event) =>
              updatePresentation('flexibleItems', event.target.value)
            }
            placeholder="اقلام منعطف مجاز، مثلاً میوه یا لبنیات"
          />
          <textarea
            className="admin-field min-h-20"
            value={settings.presentation.supplements}
            onChange={(event) =>
              updatePresentation('supplements', event.target.value)
            }
            placeholder="مکمل‌ها یا زمان مصرف (در صورت نیاز)"
          />
          <textarea
            className="admin-field min-h-20"
            value={settings.presentation.importantNotes}
            onChange={(event) =>
              updatePresentation('importantNotes', event.target.value)
            }
            placeholder="نکات مهم و اختصاصی مربی"
          />
        </div>
        <p className="mt-2 text-[10px] text-[#65716b]">
          با تغییر تعداد میان‌وعده، جدول زیر فوراً دوباره از کاتالوگ چیده
          می‌شود. برای تغییر کالری یا درصدها، «به‌روزرسانی جدول» را بزنید.
        </p>
      </section>
      <section className="rounded-2xl border border-[#e2ded5] bg-[#fbfaf6] p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <p className="text-xs font-black">سوابق رژیم‌ها</p>
            <p className="mt-1 text-[11px] text-[#71807a]">
              هر نسخه و فیدبک مربوط به آن همیشه برای مرور مربی نگه داشته می‌شود.
            </p>
          </div>
          <span className="text-xs font-black text-[#71807a]">
            {history.length} نسخه
          </span>
        </div>
        <div className="mt-3 space-y-2">
          {history.length === 0 ? (
            <p className="rounded-xl border border-dashed border-[#dedad1] p-4 text-center text-xs text-[#7a827e]">
              هنوز هیچ رژیمی برای این کاربر ساخته نشده است.
            </p>
          ) : (
            history.map((plan, index) => (
              <details
                key={plan._id}
                className="rounded-xl border border-[#e6e1d8] bg-white/75">
                <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 p-3">
                  <span>
                    <strong className="text-xs">
                      نسخه {history.length - index}
                    </strong>
                    <small className="mt-1 block text-[10px] text-[#78827d]">
                      {plan.status === 'published' ? 'منتشرشده' : 'پیش‌نویس'} ·{' '}
                      {plan.publishedAt || plan.updatedAt
                        ? new Date(
                            plan.publishedAt || plan.updatedAt,
                          ).toLocaleDateString('fa-IR')
                        : '—'}
                    </small>
                    {plan.feedback?.text && (
                      <small className="mt-2 block max-w-xl truncate rounded-lg bg-[#fff5f0] px-2 py-1 text-[10px] font-bold text-[#9b503d]">
                        فیدبک: {plan.feedback.text}
                      </small>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    <b
                      className={`rounded-lg px-2 py-1 text-[10px] font-black ${plan.status === 'published' ? 'bg-[#dceadb] text-[#315a38]' : 'bg-[#f2e5d2] text-[#8b5a28]'}`}>
                      {plan.status === 'published' ? 'منتشرشده' : 'پیش‌نویس'}
                    </b>
                    {plan.feedback?.text && (
                      <b className="rounded-lg bg-[#fff0e8] px-2 py-1 text-[10px] font-black text-[#a44d39]">
                        فیدبک دارد
                      </b>
                    )}
                  </span>
                </summary>
                <div className="border-t border-[#e6e1d8] p-3">
                  <p className="whitespace-pre-wrap text-xs leading-7 text-[#53605a]">
                    {plan.content}
                  </p>
                  {plan.feedback?.text && (
                    <div className="mt-3 rounded-lg bg-[#fff5f0] p-3 text-xs leading-6 text-[#765a50]">
                      <strong>فیدبک کاربر: </strong>
                      {plan.feedback.text}
                    </div>
                  )}
                </div>
              </details>
            ))
          )}
        </div>
      </section>
      <div className="rounded-2xl border border-[#df6b52]/25 bg-[#fff5f0] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black text-[#a44d39]">
              رژیم شخصی Start by Azi
            </p>
            <p className="mt-1 text-xs leading-6 text-[#6f625b]">
              خروجی AI فقط پیش‌نویس است. مربی وعده‌ها را انتخاب، ویرایش و سپس
              منتشر می‌کند.
            </p>
          </div>
          <button
            onClick={generate}
            disabled={generating}
            className="admin-primary disabled:opacity-50">
            <Sparkles size={16} />
            {generating ? 'در حال آماده‌سازی…' : 'ساخت پیش‌نویس با AI'}
          </button>
        </div>
      </div>
      <section className="rounded-2xl border border-[#e2ded5] bg-[#fbfaf6] p-4">
        <p className="text-xs font-black">ثبت دستی فیدبک رژیم</p>
        <p className="mt-1 text-[11px] text-[#71807a]">
          برای پیام دریافتی از واتس‌اپ یا تلفن؛ به آخرین رژیم منتشرشده متصل
          می‌شود.
        </p>
        <textarea
          value={manualFeedback}
          onChange={(event) => setManualFeedback(event.target.value)}
          className="admin-field mt-3 min-h-24"
          placeholder="متن فیدبک کاربر…"
        />
        <button
          onClick={saveManualFeedback}
          disabled={feedbackSaving || !manualFeedback.trim()}
          className="admin-secondary mt-3 disabled:opacity-50">
          {feedbackSaving ? 'در حال ثبت…' : 'ثبت فیدبک دستی'}
        </button>
      </section>
      <MealPlanEditor mealPlan={mealPlan} onChange={setMealPlan} />
      <section>
        <label className="mb-2 block text-xs font-bold text-[#52605b]">
          متن و نکات رژیم
        </label>
        <textarea
          value={content}
          onChange={(event) => setContent(event.target.value)}
          rows={12}
          className="admin-field min-h-64 resize-y leading-8"
          placeholder="رژیم قابل ویرایش کاربر…"
        />
      </section>
      <div className="rounded-2xl border border-[#d9e4d7] bg-[#f4f8f2] p-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`/users/${userId}/diet-preview`}
            className="admin-secondary">
            <ExternalLink size={16} />
            پیش‌نمایش و چاپ رژیم
          </Link>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-[#315a38]">
              مشاهده PDF بارگذاری‌شده
            </a>
          )}
          <button
            onClick={() => setShowPublishConfirm(true)}
            disabled={saving}
            className="admin-primary disabled:opacity-50">
            <Send size={16} />
            انتشار برای کاربر
          </button>
          <span className="flex-1" />
          <button
            onClick={() => save('draft')}
            disabled={saving}
            className="admin-secondary">
            <Save size={16} />
            ذخیره پیش‌نویس
          </button>
        </div>
        <p className="mt-2 text-[10px] leading-5 text-[#65716b]">
          خروجی اصلی در صفحهٔ پیش‌نمایش ساخته می‌شود و با چاپ مرورگر به PDF
          کوتاه و هم‌شکل صفحه تبدیل می‌شود.
        </p>
      </div>
      {message && (
        <p className="rounded-xl bg-[#eef3ed] p-3 text-xs font-bold text-[#315a38]">
          {message}
        </p>
      )}
      {showPublishConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#14231f]/55 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget)
              setShowPublishConfirm(false);
          }}>
          <div
            dir="rtl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="publish-confirm-title"
            className="w-full max-w-md rounded-[28px] border border-[#e4ddd2] bg-[#fffdf8] p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff0e8] text-[#b84f3a]">
                <AlertTriangle size={24} />
              </span>
              <button
                type="button"
                onClick={() => setShowPublishConfirm(false)}
                className="rounded-xl p-2 text-[#7b8581] transition hover:bg-[#f3efe7]"
                aria-label="بستن">
                <X size={18} />
              </button>
            </div>
            <h3
              id="publish-confirm-title"
              className="mt-5 text-xl font-black text-[#1c2c29]">
              انتشار رژیم برای کاربر؟
            </h3>
            <p className="mt-3 text-sm leading-7 text-[#68716d]">
              با تأیید این گزینه، نسخهٔ فعلی رژیم برای کاربر قابل مشاهده می‌شود.
              قبل از انتشار، وعده‌ها، کالری و نکات اختصاصی را یک بار دیگر بررسی
              کنید.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-start">
              <button
                type="button"
                onClick={() => setShowPublishConfirm(false)}
                className="admin-secondary justify-center">
                لغو
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowPublishConfirm(false);
                  await save('published');
                }}
                disabled={saving}
                className="admin-primary justify-center disabled:opacity-50">
                <Send size={16} />
                {saving ? 'در حال انتشار…' : 'تأیید و انتشار'}
              </button>
            </div>
          </div>
        </div>
      )}
      <details className="rounded-2xl border border-[#dedad1] bg-[#fbfaf6] p-4">
        <summary className="cursor-pointer text-sm font-black">
          <span className="inline-flex items-center gap-2">
            <Bot size={17} className="text-[#df6b52]" />
            پایگاه دانش و ظاهر PDF
          </span>
        </summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="admin-field flex items-center gap-3">
            <span className="text-xs font-bold">رنگ هدر</span>
            <input
              type="color"
              value={branding.pdfHeaderColor}
              onChange={(event) =>
                setBranding({ ...branding, pdfHeaderColor: event.target.value })
              }
              className="h-8 w-12 cursor-pointer border-0 bg-transparent p-0"
            />
          </label>
          <input
            value={branding.pdfHeaderImageUrl}
            onChange={(event) =>
              setBranding({
                ...branding,
                pdfHeaderImageUrl: event.target.value,
              })
            }
            className="admin-field"
            placeholder="لینک تصویر هدر (اختیاری)"
          />
        </div>
        <p className="mt-2 text-[10px] text-[#71807a]">
          این رنگ یا تصویر، پیش‌فرض هدر همهٔ PDFهای بعدی است.
        </p>
        <textarea
          value={knowledge}
          onChange={(event) => setKnowledge(event.target.value)}
          rows={9}
          className="admin-field mt-3 resize-y"
          placeholder="دانش و دستورالعمل‌های تأییدشده…"
        />
        <button onClick={saveKnowledge} className="admin-secondary mt-3">
          <Save size={16} />
          ذخیره تنظیمات
        </button>
      </details>
    </div>
  );
}
